<template>
  <div class="flex flex-none ml-auto">
    <div class="flex-none px-4 space-y-8">
      <div class="space-y-8">
        <div class="glue-h">
          <button class="btn btn-brand flex-1" @click="decrementMonths">Prev</button>
          <button class="btn btn-brand flex-1" @click="incrementMonths">Next</button>
        </div>
        <div class="flex space-x-6">
          <template v-for="cal in calendars">
            <div
              :key="`${cal.getFullYear()}-${cal.getMonth() + 1}-${cal.getDate()}`"
              class="space-y-2"
            >
              <div class="text-center">{{ monthList[cal.getMonth()] }} {{ cal.getFullYear() }}</div>
              <div>
                <div class="flex">
                  <div
                    v-for="date in dateList"
                    :key="date"
                    class="flex items-center justify-center w-8 h-8 p-px text-sm text-gray-500"
                  >
                    {{ date[0] }}
                  </div>
                </div>
                <Calendar :year="cal.getFullYear()" :month="cal.getMonth() + 1">
                  <template #default="{ daysByWeek }">
                    <div v-for="({ week, days }, idx) in daysByWeek" :key="week" class="flex">
                      <template v-if="idx === 0">
                        <div
                          v-for="n in 7 - days.length"
                          :key="`${week}_${n}`"
                          class="w-8 h-8 p-px"
                        />
                      </template>
                      <div
                        v-for="{ value: day, date } in days"
                        :key="`${week}_${day}`"
                        class="w-8 h-8 p-px"
                      >
                        <div
                          :class="[
                            'bg-gray-400',
                            'h-full',
                            'p-1',
                            'rounded',
                            'text-gray-600',
                            'text-xs',
                            'hover:bg-orange-500',
                            'hover:text-orange-600',
                            'cursor-pointer',
                            isDateInRange(date) ? 'bg-indigo-500 text-indigo-200' : '',
                          ]"
                          @click="updateRange(date)"
                          @dblclick="updateRange(date, true)"
                        >
                          {{ date.getDate() }}
                        </div>
                      </div>
                      <template v-if="idx !== 0">
                        <div
                          v-for="n in 7 - days.length"
                          :key="`${week}_${n}`"
                          class="w-8 h-8 p-px"
                        />
                      </template>
                    </div>
                  </template>
                </Calendar>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="flex flex-none flex-col justify-between px-4">
      <div class="select-wrapper">
        <select v-model="preset" class="select">
          <option v-for="({ name }, index) in presets" :value="index" :key="index">
            {{ name | capitalize }}
          </option>
        </select>
      </div>
      <button class="btn btn-success w-full mt-auto" @click="handleApply">Apply</button>
    </div>
  </div>
</template>

<script>
import Calendar from '@/components/Calendar';

export default {
  name: 'DateRangePicker',
  components: {
    Calendar,
  },
  data() {
    const now = new Date();
    const yesterday = new Date(now.getTime());
    yesterday.setDate(yesterday.getDate() - 1);

    const lastMonth = new Date(now.getTime());
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    return {
      calendars: [new Date(lastMonth.getTime()), new Date(now.getTime())],
      dateList: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      monthList: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
      pos: 0,
      presets: [
        {
          name: 'custom',
          range: () => this.customRange,
        },
        {
          name: 'today',
          range: () => [new Date(now.getTime()), new Date(now.getTime())],
        },
        {
          name: 'yesterday',
          range: () => [new Date(yesterday.getTime()), new Date(yesterday.getTime())],
        },
        {
          name: 'last week',
          range: () => {
            const start = new Date();
            start.setDate(start.getDate() - (start.getDay() + 6));
            const end = new Date(start.getTime());
            end.setDate(end.getDate() + 6);
            return [new Date(start.getTime()), new Date(end.getTime())];
          },
        },
        {
          name: 'last month',
          range: () => {
            const end = new Date();
            end.setDate(end.getDate() - end.getDate());
            const start = new Date(end.getTime());
            start.setDate(1);
            return [new Date(start.getTime()), new Date(end.getTime())];
          },
        },
        {
          name: 'last 7 days',
          range: () => {
            const end = new Date();
            end.setDate(end.getDate() - 1);
            const start = new Date();
            start.setDate(end.getDate() - 6);
            return [new Date(start.getTime()), new Date(end.getTime())];
          },
        },
        {
          name: 'last 30 days',
          range: () => {
            const end = new Date();
            end.setDate(end.getDate() - 1);
            const start = new Date();
            start.setDate(end.getDate() - 29);
            return [new Date(start.getTime()), new Date(end.getTime())];
          },
        },
      ],
      preset: 5,
      customRange: [new Date(), new Date()],
    };
  },
  computed: {
    selectedRange() {
      return this.presets[this.preset].range();
    },
    formattedRange() {
      const [since, until] = this.selectedRange;
      const zeroPrefixedString = (val) => [0, ...`${val}`.split('')].splice(-2).join('');
      const partsSince = [
        since.getFullYear(),
        zeroPrefixedString(since.getMonth() + 1),
        zeroPrefixedString(since.getDate()),
      ];
      const partsUntil = [
        until.getFullYear(),
        zeroPrefixedString(until.getMonth() + 1),
        zeroPrefixedString(until.getDate()),
      ];
      return {
        since: partsSince.join('-'),
        until: partsUntil.join('-'),
      };
    },
  },
  created() {
    this.$emit('created', this.formattedRange);
  },
  methods: {
    decrementMonths() {
      const [last] = this.calendars;
      const first = new Date(last.getTime());
      first.setMonth(first.getMonth() - 1);
      this.calendars = [first, last];
    },
    incrementMonths() {
      const [first] = this.calendars.splice(-1);
      const last = new Date(first.getTime());
      last.setMonth(last.getMonth() + 1);
      this.calendars = [first, last];
    },
    isDateInRange(date) {
      const [since, until] = this.selectedRange;
      if (!(since instanceof Date)) return false;
      if (!(until instanceof Date)) {
        return this.getNormalizedTime(date) === this.getNormalizedTime(since);
      }
      return (
        this.getNormalizedTime(date) >= this.getNormalizedTime(since) &&
        this.getNormalizedTime(date) <= this.getNormalizedTime(until)
      );
    },
    getNormalizedTime(date) {
      const nDate = new Date(date.getTime());
      nDate.setMilliseconds(0);
      nDate.setSeconds(0);
      nDate.setMinutes(0);
      nDate.setHours(0);
      return nDate.getTime();
    },
    updateRange(date, all = false) {
      if (all) {
        this.customRange.splice(this.pos, 1, new Date(this.getNormalizedTime(date)));
        this.pos = (this.pos + 1) % 2;
        this.customRange.splice(this.pos, 1, new Date(this.getNormalizedTime(date)));
      } else {
        this.customRange.splice(this.pos, 1, new Date(this.getNormalizedTime(date)));
        const [first, last] = this.customRange;
        if (first.getTime() > last.getTime()) {
          this.customRange = [last, first];
          this.pos = 0;
        } else {
          this.pos = (this.pos + 1) % 2;
        }
        if (this.preset !== 0) {
          this.preset = 0;
          this.updateRange(date);
        }
      }
    },
    handleApply() {
      this.$emit('apply', this.formattedRange);
    },
  },
};
</script>
